import { CONTENT_ASSETS, CONTENT_TEMPLATE_PRESETS, createContentDocument, parseContentDocument, serializeContentDocument } from './document.mjs';
import { renderContentDocument } from './renderer.mjs';
import { listLocalDrafts, saveLocalDraft, readLocalDraft } from './persistence.mjs';
import { createStudioClient, updateCourseLecture } from './client.mjs';
import { arrangeSourceText } from './material.mjs';
import { mountWorkspace } from './workspace.mjs';

const KINDS = { cards: '개념 카드', text: '설명', steps: '순서', quote: '핵심 문장', image: '그림', exercise: '생각과 연습' };
function element(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'text') node.textContent = value;
    else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
    else if (value !== undefined) node.setAttribute(key, String(value));
  }
  for (const child of children) node.append(child);
  return node;
}
const button = (text, action, attrs = {}) => element('button', { type: 'button', text, onclick: action, ...attrs });
const option = (value, text) => element('option', { value, text });
const clone = (value) => structuredClone(value);

export function mountContentStudio(root, options = {}) {
  return mountWorkspace(root, options);
}

export function mountTemplateStudio(root, { initialMarkdown = '', onChange, getAccessToken } = {}) {
  let content;
  content = parseContentDocument(initialMarkdown);
  if (!content) content = initialMarkdown.trim()
    ? arrangeSourceText({ topic: '', sourceText: initialMarkdown, templateId: 'learning-editorial' })
    : createContentDocument('learning-editorial');
  let id = crypto.randomUUID();
  let revision = 0;
  let dirty = false;
  let busy = false;
  let records = [];
  let courseDraft = null;
  let courseSlug = '';
  const client = createStudioClient({ getAccessToken });
  root.classList.add('es-studio');

  const status = element('p', { role: 'status', 'aria-live': 'polite', 'data-testid': 'studio-status', class: 'es-studio-status', text: '새 문서 · 이 기기에 저장할 수 있습니다' });
  const errorBox = element('p', { role: 'alert', hidden: '', 'data-testid': 'studio-error', class: 'es-studio-error' });
  const preview = element('div', { class: 'es-studio-preview', 'data-testid': 'studio-preview', 'aria-label': '학습자 미리보기' });
  const editor = element('div', { class: 'es-studio-fields' });
  const draftSelect = element('select', { 'aria-label': '저장한 문서', 'data-testid': 'studio-drafts' });
  const template = element('select', { 'aria-label': '템플릿', 'data-testid': 'studio-template' }, CONTENT_TEMPLATE_PRESETS.map((preset) => option(preset.id, preset.label)));
  const fail = (error) => { errorBox.textContent = error instanceof Error ? error.message : '요청을 완료하지 못했습니다.'; errorBox.hidden = false; };
  const clearError = () => { errorBox.hidden = true; errorBox.textContent = ''; };
  const announce = (text) => { status.textContent = text; };
  const changed = () => {
    dirty = true;
    announce('편집 중 · 아직 저장하지 않았습니다');
    preview.innerHTML = renderContentDocument(content);
    onChange?.(serializeContentDocument(content));
  };
  const mayReplace = () => !dirty || window.confirm('저장하지 않은 편집 내용이 있습니다. 다른 내용으로 바꿀까요?');
  function field(label, value, change, { multiline = false, testid, placeholder = '' } = {}) {
    const input = element(multiline ? 'textarea' : 'input', {
      ...(multiline ? { rows: 3 } : { type: 'text' }), 'data-testid': testid, placeholder,
      oninput: (event) => { change(event.target.value); changed(); },
    });
    input.value = value;
    return element('label', { class: 'es-studio-field' }, [element('span', { text: label }), input]);
  }
  function assetField(owner) {
    const select = element('select', { 'aria-label': '그림 선택', onchange: (event) => {
      if (event.target.value) owner.assetId = event.target.value;
      else delete owner.assetId;
      changed();
    } }, [option('', '그림 없음'), ...CONTENT_ASSETS.map((asset) => option(asset.id, asset.label))]);
    select.value = owner.assetId ?? '';
    return element('label', { class: 'es-studio-field' }, [element('span', { text: '내용에 맞는 그림' }), select]);
  }
  function renderEditor() {
    template.value = content.templateId;
    const fields = [
      field('제목', content.title, (value) => { content.title = value; }, { multiline: true, testid: 'studio-title', placeholder: '독자에게 전할 이야기' }),
      field('부제', content.subtitle, (value) => { content.subtitle = value; }),
      field('상단 안내', content.kicker, (value) => { content.kicker = value; }),
    ];
    content.sections.forEach((section, sectionIndex) => {
      const box = element('fieldset', { class: 'es-studio-section' }, [element('legend', { text: `${String(sectionIndex + 1).padStart(2, '0')} · ${KINDS[section.kind]}` })]);
      box.append(field('섹션 제목', section.title, (value) => { section.title = value; }));
      if (section.kind !== 'image') box.append(field('본문', section.body ?? '', (value) => { section.body = value; }, { multiline: true }));
      if (section.kind === 'image') box.append(assetField(section), field('그림 설명', section.alt ?? '', (value) => { section.alt = value; }));
      if (['cards', 'steps', 'exercise'].includes(section.kind)) {
        (section.items ?? []).forEach((item, itemIndex) => {
          const itemBox = element('div', { class: 'es-studio-item' }, [
            field(`항목 ${itemIndex + 1} 제목`, item.title, (value) => { item.title = value; }),
            field('항목 내용', item.body, (value) => { item.body = value; }, { multiline: true }), assetField(item),
            button('항목 삭제', () => { section.items.splice(itemIndex, 1); changed(); renderEditor(); }, { class: 'es-studio-text-button', 'aria-label': `${sectionIndex + 1}번 섹션 항목 ${itemIndex + 1} 삭제` }),
          ]);
          box.append(itemBox);
        });
        box.append(button('+ 항목 추가', () => { section.items ??= []; section.items.push({ title: '', body: '' }); changed(); renderEditor(); }));
      }
      const actions = element('div', { class: 'es-studio-row' });
      if (sectionIndex > 0) actions.append(button('위로', () => { [content.sections[sectionIndex - 1], content.sections[sectionIndex]] = [content.sections[sectionIndex], content.sections[sectionIndex - 1]]; changed(); renderEditor(); }, { 'aria-label': `${sectionIndex + 1}번 섹션 위로` }));
      actions.append(button('섹션 삭제', () => { content.sections.splice(sectionIndex, 1); changed(); renderEditor(); }, { class: 'es-studio-text-button', 'aria-label': `${sectionIndex + 1}번 섹션 삭제` }));
      box.append(actions);
      fields.push(box);
    });
    const kind = element('select', { 'aria-label': '추가할 섹션 종류' }, Object.entries(KINDS).map(([key, label]) => option(key, label)));
    fields.push(element('div', { class: 'es-studio-row' }, [kind, button('+ 섹션', () => {
      const section = { id: crypto.randomUUID(), kind: kind.value, title: '' };
      if (['cards', 'steps', 'exercise'].includes(kind.value)) section.items = [{ title: '', body: '' }];
      else if (kind.value === 'image') { section.assetId = 'study-scene'; section.alt = ''; }
      else section.body = '';
      content.sections.push(section); changed(); renderEditor();
    }, { 'data-testid': 'studio-add-section' })]));
    fields.push(field('하단 안내 · 출처', content.footer, (value) => { content.footer = value; }, { multiline: true }));
    editor.replaceChildren(...fields);
  }
  function replace(document, { saved = false } = {}) {
    serializeContentDocument(document);
    content = clone(document);
    renderEditor();
    changed();
    if (saved) dirty = false;
  }
  async function run(action) {
    if (busy) return;
    busy = true; clearError();
    const controls = [...root.querySelectorAll('button,input,textarea,select')];
    const original = controls.map((control) => control.disabled);
    controls.forEach((control) => { control.disabled = true; });
    root.setAttribute('aria-busy', 'true');
    try { await action(); } catch (error) { fail(error); announce('완료하지 못했습니다 · 작성한 내용은 유지됩니다'); }
    finally { controls.forEach((control, index) => { control.disabled = original[index]; }); root.removeAttribute('aria-busy'); busy = false; }
  }
  async function refreshDrafts() {
    records = await listLocalDrafts();
    draftSelect.replaceChildren(option('', '저장한 문서 선택'), ...records.map((record) => option(record.id, `${record.title} · ${new Date(record.updatedAt).toLocaleDateString('ko-KR')}`)));
    draftSelect.value = records.some((record) => record.id === id) ? id : '';
  }
  const save = button('기기에 저장', () => run(async () => {
    const result = await saveLocalDraft({ id, revision, document: content });
    revision = result.revision; dirty = false; await refreshDrafts(); announce(`기기에 저장됨 · ${new Date(result.updatedAt).toLocaleTimeString('ko-KR')}`);
  }), { 'data-testid': 'studio-save', class: 'es-studio-primary' });
  const header = element('header', { class: 'es-studio-header' }, [
    element('a', { href: '/ko/learn', class: 'es-studio-brand', text: 'EduSense / 제작실' }),
    element('div', { class: 'es-studio-header-actions' }, [
      button('새 문서', () => { if (!mayReplace()) return; id = crypto.randomUUID(); revision = 0; replace(createContentDocument(template.value)); }, { 'data-testid': 'studio-new' }), save,
    ]),
  ]);
  const intro = element('div', { class: 'es-studio-intro' }, [element('p', { class: 'es-studio-eyebrow', text: 'CONTENT ATELIER' }), element('h1', { text: '지식에, 읽고 싶은 형태를.' }), element('p', { text: '자료의 흐름을 잡고, 그림과 문장을 다듬어 나만의 교재를 완성하세요.' })]);
  const templatePanel = element('section', { class: 'es-studio-panel' }, [element('h2', { text: '01  형태를 고르세요' }), template,
    button('사주 원본 예시 열기', () => { if (!mayReplace()) return; id = crypto.randomUUID(); revision = 0; replace(createContentDocument('saju-editorial', { example: 'wealth-flow' })); announce('재물운 원본 편집 샘플 · 내용을 바꿔 나만의 문서를 만드세요'); }, { 'data-testid': 'studio-sample', class: 'es-studio-text-button' }),
    button('학습 구성 예시 열기', () => { if (!mayReplace()) return; id = crypto.randomUUID(); revision = 0; replace(createContentDocument('learning-editorial', { example: 'learning-flow' })); }, { 'data-testid': 'studio-learning-sample', class: 'es-studio-text-button' }),
    button('분수 수업 예시 열기', () => { if (!mayReplace()) return; id = crypto.randomUUID(); revision = 0; replace(createContentDocument('learning-editorial', { example: 'fraction-addition' })); }, { 'data-testid': 'studio-fraction-sample', class: 'es-studio-text-button' }),
  ]);
  template.addEventListener('change', () => { content.templateId = template.value; changed(); });
  const topic = element('input', { type: 'text', placeholder: '예: 한국어 수업 — 정중하게 부탁하기', 'data-testid': 'studio-topic' });
  const source = element('textarea', { rows: 7, placeholder: '수업 노트, 해설, 참고 자료를 붙여 넣으세요.\n## 제목으로 내용을 나눌 수 있습니다.', 'data-testid': 'studio-source' });
  const sourcePanel = element('details', { class: 'es-studio-panel', open: '' }, [element('summary', { text: '02  주제와 자료로 시작하기' }),
    element('label', { class: 'es-studio-field' }, [element('span', { text: '주제' }), topic]),
    element('label', { class: 'es-studio-field' }, [element('span', { text: '원문 자료' }), source]),
    element('div', { class: 'es-studio-row' }, [button('원문으로 구성', () => {
      clearError(); if (!mayReplace()) return;
      try { replace(arrangeSourceText({ topic: topic.value, sourceText: source.value, templateId: template.value })); announce('입력한 원문으로 구성했습니다 · 그림과 섹션을 다듬어 주세요'); } catch (error) { fail(error); }
    }, { 'data-testid': 'studio-arrange' }), button('AI 초안 생성', () => {
      if (!topic.value.trim()) { fail(new Error('AI로 만들 주제를 입력해 주세요.')); return; }
      if (!mayReplace()) return;
      run(async () => { announce('AI가 주제와 자료를 바탕으로 작성하고 있습니다…'); const generated = await client.generate({ topic: topic.value.trim(), sourceText: source.value, templateId: template.value }); replace(generated); announce('AI 초안 생성됨 · 사실과 출처를 검토한 후 저장해 주세요'); });
    }, { 'data-testid': 'studio-generate' })]),
    element('p', { class: 'es-studio-hint', text: '원문 구성은 입력한 문장을 배치합니다. AI 생성은 관리자 로그인이 필요합니다.' }),
  ]);
  const importFile = element('input', { type: 'file', accept: '.md,.json,text/markdown,application/json', 'data-testid': 'studio-import', onchange: (event) => run(async () => {
    const file = event.target.files[0]; if (!file) return;
    if (file.size > 2_000_000) throw new Error('2MB 이하의 콘텐츠 파일을 선택해 주세요.');
    const text = await file.text();
    const document = file.name.endsWith('.json') ? JSON.parse(text) : parseContentDocument(text);
    if (!document) throw new Error('EduSense에서 내보낸 콘텐츠 파일을 선택해 주세요. 일반 원문은 자료 입력란에 넣어 주세요.');
    serializeContentDocument(document);
    if (!mayReplace()) return;
    id = crypto.randomUUID(); revision = 0; replace(document); announce('파일을 열었습니다 · 저장 전 내용을 확인해 주세요'); event.target.value = '';
  }) });
  const library = element('details', { class: 'es-studio-panel' }, [element('summary', { text: '저장한 문서와 파일' }), draftSelect,
    button('문서 열기', () => run(async () => {
      const record = records.find((entry) => entry.id === draftSelect.value);
      if (!record) throw new Error('저장한 문서를 선택해 주세요.');
      if (!mayReplace()) return;
      const document = readLocalDraft(record); id = record.id; revision = record.revision; replace(document, { saved: true }); announce(`기기 문서 열림 · 저장 버전 ${revision}`);
    }), { 'data-testid': 'studio-open' }),
    button('파일로 내보내기', () => {
      const url = URL.createObjectURL(new Blob([serializeContentDocument(content)], { type: 'text/markdown;charset=utf-8' }));
      const link = element('a', { href: url, download: `${(content.title || 'edusense-content').replace(/[\\/:*?"<>|]/g, '-')}.md` });
      link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); announce('콘텐츠 파일을 내보냈습니다');
    }, { 'data-testid': 'studio-export' }), element('label', { class: 'es-studio-field' }, [element('span', { text: '콘텐츠 파일 가져오기' }), importFile]),
    element('p', { class: 'es-studio-hint', text: '기기 저장은 현재 브라우저에 보관됩니다. 다른 기기로 옮길 때는 파일을 내보내세요.' }),
  ]);
  const slug = element('input', { placeholder: 'my-course', type: 'text', 'aria-label': '강좌 주소' });
  const lectureSelect = element('select', { 'aria-label': '편집할 강의' });
  const cmsState = element('p', { class: 'es-studio-hint', text: '관리자 계정으로 강좌를 열어 저장합니다. 공개된 강좌는 저장한 내용이 학습자 화면에도 반영될 수 있습니다.' });
  const cmsPanel = element('details', { class: 'es-studio-panel' }, [element('summary', { text: '강좌에 연결' }), slug,
    button('강좌 열기 / 만들기', () => run(async () => {
      const next = await client.open(slug.value.trim());
      courseDraft = next; courseSlug = slug.value.trim();
      lectureSelect.replaceChildren(option('', '새 강의로 추가'), ...next.document.sections.flatMap((section) => section.lectures.map((lecture) => option(lecture.id, `${section.title} / ${lecture.title}`))));
      cmsState.textContent = `${next.document.title} · 버전 ${next.revision}. 공개된 강좌는 저장한 내용이 학습자 화면에도 반영될 수 있습니다.`;
      announce('강좌를 열었습니다. 새 강의를 추가하거나 편집할 강의를 선택하세요.');
    })), lectureSelect,
    button('선택 강의 가져오기', () => {
      clearError();
      try {
        if (!courseDraft || !lectureSelect.value) throw new Error('먼저 강좌와 강의를 선택해 주세요.');
        const lecture = courseDraft.document.sections.flatMap((section) => section.lectures).find((entry) => entry.id === lectureSelect.value);
        const document = parseContentDocument(lecture.markdown);
        if (!document) throw new Error('일반 마크다운 강의입니다. 원문 자료란에 복사하여 구성해 주세요.');
        if (mayReplace()) { id = crypto.randomUUID(); revision = 0; replace(document); }
      } catch (error) { fail(error); }
    }),
    button('강좌에 저장', () => run(async () => {
      if (!courseDraft) throw new Error('먼저 강좌를 열어 주세요.');
      if (!content.title.trim() || !content.sections.length) throw new Error('제목과 내용을 작성해 주세요.');
      let next;
      let lectureId = lectureSelect.value;
      if (lectureId) next = updateCourseLecture(courseDraft.document, lectureId, content);
      else {
        next = clone(courseDraft.document);
        if (!next.sections.length) { next.sections.push({ id: crypto.randomUUID(), title: '학습 자료', order: 0, lectures: [] }); next.title = content.title; }
        const section = next.sections[0];
        lectureId = crypto.randomUUID();
        section.lectures.push({ id: lectureId, title: content.title, order: section.lectures.length, durationSeconds: 0, markdown: serializeContentDocument(content) });
      }
      const saved = await client.save(courseSlug, courseDraft.revision, next);
      courseDraft = saved;
      lectureSelect.replaceChildren(option('', '새 강의로 추가'), ...saved.document.sections.flatMap((section) => section.lectures.map((lecture) => option(lecture.id, `${section.title} / ${lecture.title}`))));
      lectureSelect.value = lectureId;
      cmsState.textContent = `${saved.document.title} · 초안 버전 ${saved.revision}`;
      announce('강좌에 저장했습니다. 공개된 강좌는 학습자 화면에서도 내용을 확인해 주세요.');
    }), { 'data-testid': 'studio-cms-save' }), cmsState,
    element('a', { href: '/ko/login', text: '관리자 계정으로 로그인' }),
  ]);
  const sidebar = element('aside', { class: 'es-studio-sidebar', 'aria-label': '콘텐츠 작성' }, [templatePanel, sourcePanel, library, cmsPanel, element('section', { class: 'es-studio-panel' }, [element('h2', { text: '03  문장과 그림 다듬기' }), editor])]);
  const stage = element('section', { class: 'es-studio-stage', 'aria-label': '문서 미리보기' }, [element('div', { class: 'es-studio-stage-label' }, [element('span', { text: '독자가 보게 될 페이지' }), button('보기 확대', () => { root.classList.toggle('es-studio-reading'); }, { 'aria-label': '미리보기 확대 또는 축소' })]), preview]);
  root.replaceChildren(header, intro, status, errorBox, element('div', { class: 'es-studio-workspace' }, [sidebar, stage]));
  renderEditor(); preview.innerHTML = renderContentDocument(content);
  refreshDrafts().catch(fail);
  const beforeUnload = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
  window.addEventListener('beforeunload', beforeUnload);
  return {
    getDocument: () => clone(content),
    setDocument: (document) => replace(document),
    destroy: () => { window.removeEventListener('beforeunload', beforeUnload); root.replaceChildren(); },
  };
}

const standalone = typeof document === 'undefined' ? null : document.getElementById('content-studio-root');
if (standalone) {
  const example = new URLSearchParams(location.search).get('example');
  const preset = CONTENT_TEMPLATE_PRESETS.find((entry) => entry.examples.some((item) => item.id === example));
  const initialMarkdown = preset ? serializeContentDocument(createContentDocument(preset.id, { example })) : '';
  const legacy = new URLSearchParams(location.search).get('editor') === 'template';
  // The standalone Studio owns its address: /learn/studio/<template> (studio-address.mjs).
  (legacy ? mountTemplateStudio : mountContentStudio)(standalone, { initialMarkdown, routing: !legacy });
}
